import { Text, View } from '@react-pdf/renderer';

import { AdditionalStructureKey } from '../../../redux/features/selectors/commentarySelectors/selectAdditionalStructureCommentary.ts';
import { COMMENTARY_TEXT } from '../../commentary/consts.ts';
import { ExportCommentaryTable } from '../../commentary/export/ExportCommentaryTable.tsx';
import { CommentarySection, MultiCommentarySection } from '../components/CommentarySection.tsx';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { depreciatedCostAdjustmentsPageStyles, fontStyles } from '../styles.ts';
import { DepreciatedCostAdjustmentsPageProps } from '../types.ts';
import { CompType } from '@aloft/toolkit-shared';

const DepreciatedCostAdjustmentsPageComponent = ({
  subjectAddress,
  adjustmentsTableData,
  compType,
  hasFunctionalDepreciation,
  hasExternalDepreciation,
  commentaryV2,
}: {
  subjectAddress: string;
  adjustmentsTableData: DepreciatedCostAdjustmentsPageProps['adjustmentsTableData'] | DepreciatedCostAdjustmentsPageProps['arvAdjustmentsTableData'];
  compType?: CompType;
  hasFunctionalDepreciation: boolean;
  hasExternalDepreciation: boolean;
  commentaryV2: DepreciatedCostAdjustmentsPageProps['commentaryV2'];
}) => {
  const { title, subtitle, subItems } = COMMENTARY_TEXT['dep-cost'];

  const {
    mainDwelling,
    quality,
    condition,
    functionalDepreciation,
    externalDepreciation,
    additionalStructures,
  } = adjustmentsTableData;

  const costBreakdownItems = [mainDwelling.gla, mainDwelling.fullBath, mainDwelling.halfBath];
  if (mainDwelling.basementTotal) costBreakdownItems.push(mainDwelling.basementTotal);
  if (mainDwelling.basementFinished) costBreakdownItems.push(mainDwelling.basementFinished);

  return (
    <Tkv2PDFPage title={title} address={subjectAddress} badge={compType}>
      <Text style={fontStyles.body}>{subtitle}</Text>

      {/* Main Dwelling */}
      <Text style={fontStyles.h4}>{subItems['main-dwelling'].title}</Text>
      <Text style={fontStyles.body}>{subItems['main-dwelling'].subtitle}</Text>

      <View style={depreciatedCostAdjustmentsPageStyles.mainDwellingCostBreakdownContainer}>
        {costBreakdownItems.map((item) => (
          <View
            key={item.title}
            style={depreciatedCostAdjustmentsPageStyles.roundingAndThresholdsItem}
          >
            <View style={depreciatedCostAdjustmentsPageStyles.roundingAndThresholdsItemTopRow}>
              <Text style={fontStyles.h5}>{item.title}</Text>
              <Text style={fontStyles.body}>{item.value}</Text>
            </View>
            <Text style={fontStyles.small}>{item.formula}</Text>
          </View>
        ))}
      </View>

      {/* Rounding and Thresholds */}
      {mainDwelling.roundingAndThresholds.length > 0 && (
        <>
          <Text style={fontStyles.h4}>{subItems.roundingAndThresholds.title}</Text>
          <View>
            {mainDwelling.roundingAndThresholds.map((item) => (
              <Text key={item} style={fontStyles.body}>
                {item}
              </Text>
            ))}
          </View>
        </>
      )}

      <MultiCommentarySection
        sections={[
          {
            title: 'GLA Commentary',
            commentary: commentaryV2.depreciatedCost?.glaCommentary,
          },
          {
            title: 'Bath Commentary',
            commentary: commentaryV2.depreciatedCost?.bathCommentary,
          },
          ...(mainDwelling.basementTotal || mainDwelling.basementFinished
            ? [
              {
                title: 'Basement Commentary',
                commentary: commentaryV2.depreciatedCost?.basementCommentary,
              },
            ]
            : []),
        ]}
      />

      {/* Quality */}
      <Text style={fontStyles.h4}>{subItems.quality.title}</Text>
      <Text style={fontStyles.body}>{subItems.quality.subtitle}</Text>

      <ExportCommentaryTable {...quality} configKey="quality" />

      <CommentarySection
        title="Quality Commentary"
        commentary={commentaryV2.depreciatedCost?.qualityCommentary}
      />

      {/* Condition */}
      <Text style={fontStyles.h4}>{subItems.condition.title}</Text>
      <Text style={fontStyles.body}>{subItems.condition.subtitle}</Text>

      <ExportCommentaryTable {...condition} configKey="condition" />

      <CommentarySection
        title="Condition Commentary"
        commentary={commentaryV2.depreciatedCost?.conditionCommentary}
      />

      {/* Functional Depreciation */}
      {hasFunctionalDepreciation && (
        <>
          <Text style={fontStyles.h4}>{subItems.functionalDepreciation.title}</Text>
          <Text style={fontStyles.body}>{subItems.functionalDepreciation.subtitle}</Text>

          <ExportCommentaryTable {...functionalDepreciation} configKey="functionalDepreciation" />
        </>
      )}

      {/* External Depreciation */}
      {hasExternalDepreciation && (
        <>
          <Text style={fontStyles.h4}>{subItems.externalDepreciation.title}</Text>
          <Text style={fontStyles.body}>{subItems.externalDepreciation.subtitle}</Text>

          <ExportCommentaryTable {...externalDepreciation} configKey="externalDepreciation" />
        </>
      )}

      {/* Additional Structures */}
      <Text style={fontStyles.h4}>{subItems.additionalStructures.title}</Text>
      <Text style={fontStyles.body}>{subItems.additionalStructures.subtitle}</Text>

      {additionalStructures.map(({ key, label, tableData, helperText }) => {
        const keysWithCommentary: AdditionalStructureKey[] = [
          'poolSpa',
          'adu',
          'garageCarport',
          'porchPatioDeck',
          'shed',
        ];

        type CommentaryKey =
          | Extract<AdditionalStructureKey, 'adu' | 'garageCarport' | 'porchPatioDeck' | 'shed'>
          | 'pool';

        const commentaryKey = key === 'poolSpa' ? 'pool' : (key as CommentaryKey);

        return (
          <View key={key} style={depreciatedCostAdjustmentsPageStyles.additionalStructure}>
            <Text style={fontStyles.h5}>{label}</Text>

            <ExportCommentaryTable
              data={tableData}
              configKey="additionalStructures"
              helperText={helperText}
            />
            {keysWithCommentary.includes(key) && (
              <CommentarySection
                title={`${label} Commentary`}
                commentary={commentaryV2.depreciatedCost?.[`${commentaryKey}Commentary`]}
              />
            )}
          </View>
        );
      })}
    </Tkv2PDFPage>
  );
}

export const DepreciatedCostAdjustmentsPage = ({
  subjectAddress,
  commentaryV2,
  adjustmentsTableData,
  arvAdjustmentsTableData,
  hasArvOption,
  hasFunctionalDepreciation,
  hasExternalDepreciation,
}: DepreciatedCostAdjustmentsPageProps) => {
  return (
    <>
      <DepreciatedCostAdjustmentsPageComponent
        subjectAddress={subjectAddress}
        commentaryV2={commentaryV2}
        adjustmentsTableData={adjustmentsTableData}
        compType={hasArvOption ? 'aiv' : undefined}
        hasFunctionalDepreciation={hasFunctionalDepreciation}
        hasExternalDepreciation={hasExternalDepreciation}
      />
      {hasArvOption && (
        <DepreciatedCostAdjustmentsPageComponent
          subjectAddress={subjectAddress}
          commentaryV2={commentaryV2}
          adjustmentsTableData={arvAdjustmentsTableData}
          compType="arv"
          hasFunctionalDepreciation={hasFunctionalDepreciation}
          hasExternalDepreciation={hasExternalDepreciation}
        />
      )}
    </>
  )
};
